<!-- eslint-disable vue/no-v-html -->
<template>
  <ClientOnly>
  <div class="code-block" data-testid="code-block">
    <div class="code-toolbar flex min-h-10 justify-between gap-4 px-4 py-1.5 font-mono text-sm" data-testid="code-toolbar">
      <div v-if="displayFilename" class="filename place-self-center" :class="resolveIcon(displayFilename)">
        <span :class="displayFilename.length > 35 && 'text-xs'">{{ displayFilename }}</span>
      </div>
      <div v-else class="place-self-center">
        code
      </div>
      <button type="button" class="copyToolbar grid cursor-pointer items-center font-normal" data-testid="copy-code" :aria-label="copied ? 'Code copied' : 'Copy code'" @click="copyActiveCode()">
        <div class="inline-block">
          <Transition name="fade" mode="out-in">
            <div v-if="!copied">
              <div class="copyLabel inline transition-all text-xs uppercase tracking-wide">
                Copy
              </div>
              <IconCopy class="hidden h-4 w-4 md:inline ml-2" />
            </div>
            <div v-else class="text-xs uppercase tracking-wide">
              Copied
            </div>
          </Transition>
        </div>
      </button>
    </div>
    <!-- Tabs -->
    <div v-if="tabs.length > 1" class="code-tabs flex flex-wrap gap-0" data-testid="code-tabs">
      <button
        v-for="(tab, index) in tabs"
        :key="tab.id"
        type="button"
        class="code-tab pr-4 pl-3 py-2 text-sm font-medium transition-colors"
        :class="{ 'is-active': activeTab === index }"
        :aria-pressed="activeTab === index"
        @click="activeTab = index"
      >
        <span
          class="filename pl-7"
          :class="tab.filename ? resolveIcon(tab.filename) : []"
        >{{ tab.filename || tab.label }}</span>
      </button>
    </div>
    <div class="code-scroll" :class="{ 'code-scroll-wrap': wrapsText }">
      <pre
        :class="['language-' + language, { 'language-wrap': wrapsText }]"
        :style="wrapsText ? { '--line-number-ch': String(lineNumberDigits) } : undefined"
        v-html="formattedCode"
      />
    </div>
  </div>
  </ClientOnly>
</template>

<script setup lang="ts">
import { useClipboard } from '@vueuse/core'
import * as Prism from 'prismjs'
import 'prismjs/components/prism-json'
import 'prismjs/components/prism-typescript'
import 'prismjs/components/prism-bash'
import 'prismjs/components/prism-yaml'
import 'prismjs/components/prism-gherkin'
import 'prismjs/components/prism-jsx'
import 'prismjs/components/prism-tsx'
import 'prismjs/components/prism-diff'
import 'prismjs/components/prism-markdown'
import '@/helpers/prismjs/prism-treeview'
import { resolveIcon } from '@/helpers/resolveIcon'
import { punctuation } from '@/helpers/prismjs/punctuation'
import { string } from '@/helpers/prismjs/string'

interface Tab {
  id: string
  label: string
  filename: string | null
  code: string
}

const props = defineProps({
  code: {
    type: String,
    default: ''
  },
  language: {
    type: String,
    default: 'json'
  },
  filename: {
    type: String,
    default: null
  },
  highlights: {
    type: Array as () => number[],
    default: () => []
  }
})

const activeTab = ref(0)

// Parse tabs from filename prop
// Format: [win, mac] or [win:file.js, mac:file.js]
// Note: Nuxt Content may strip brackets, so we also handle: win, mac
const tabs = computed<Tab[]>(() => {
  if (!props.filename) {
    return [{ id: 'default', label: '', filename: null, code: props.code }]
  }

  // Check if filename contains tab syntax
  // With brackets: [tab1, tab2] or [tab1:file1, tab2:file2]
  // Without brackets (stripped by parser): tab1, tab2
  const tabMatch = props.filename.match(/^\[(.+)\]$/)
  const hasComma = props.filename.includes(',')

  if (!tabMatch && !hasComma) {
    // No tabs, regular filename
    return [{ id: 'default', label: '', filename: props.filename, code: props.code }]
  }

  // Parse tab definitions - each item is a filename
  const tabString = tabMatch ? tabMatch[1] : props.filename
  const tabDefs = tabString.split(',').map(t => t.trim())
  const tabList: Tab[] = tabDefs.map(def => ({
    id: def,
    label: def,
    filename: def,
    code: ''
  }))

  // Split code by tab markers (// tabId comments)
  const codeLines = props.code.split('\n')
  let currentTabIndex = -1
  const tabCodeMap: Record<string, string[]> = {}

  // Initialize code arrays for each tab
  tabList.forEach(tab => {
    tabCodeMap[tab.id] = []
  })

  for (const line of codeLines) {
    // Check if line is a tab marker
    // Supports: // tabId (JS/TS) or # tabId (bash/shell)
    const markerMatch = line.match(/^(?:\/\/|#)\s*(\S+)\s*$/)
    if (markerMatch) {
      const markerId = markerMatch[1]
      const tabIndex = tabList.findIndex(t => t.id === markerId)
      if (tabIndex > -1) {
        currentTabIndex = tabIndex
        continue // Skip the marker line itself
      }
    }

    // Add line to current tab's code
    if (currentTabIndex > -1 && tabList[currentTabIndex]) {
      tabCodeMap[tabList[currentTabIndex].id].push(line)
    }
  }

  // Assign code to tabs and trim trailing empty lines
  tabList.forEach(tab => {
    let lines = tabCodeMap[tab.id]
    // Remove trailing empty lines
    while (lines.length > 0 && lines[lines.length - 1].trim() === '') {
      lines.pop()
    }
    tab.code = lines.join('\n')
  })

  return tabList
})

// Get filename to display (current tab's filename or null)
const displayFilename = computed(() => {
  if (tabs.value.length <= 1) {
    return props.filename
  }
  return null
})

// Get active tab's code
const activeCode = computed(() => {
  return tabs.value[activeTab.value]?.code || props.code
})

// Clipboard functionality for active tab's code
const clipboardSource = computed(() => activeCode.value)
const { copy, copied } = useClipboard({ source: clipboardSource })

const copyActiveCode = () => {
  copy(activeCode.value)
}

const TEXT_WRAP_LANGUAGES = ['plain', 'plaintext', 'markdown', 'md', 'diff']

const wrapsText = computed(() => TEXT_WRAP_LANGUAGES.includes(props.language))

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

const sourceLines = computed(() => trimTrailingEmptyLine(activeCode.value.split('\n')))

const lineNumberDigits = computed(() =>
  Math.max(2, String(sourceLines.value.length).length)
)

const wrapNumberedLine = (line: string, num: number, extraClass = '') => {
  const classes = [
    extraClass,
    props.highlights.includes(num + 1) ? 'highlight' : ''
  ].filter(Boolean).join(' ')

  const number = (num + 1).toString().padStart(lineNumberDigits.value, ' ')

  if (wrapsText.value) {
    const lineClasses = ['code-line', extraClass, props.highlights.includes(num + 1) ? 'highlight' : '']
      .filter(Boolean)
      .join(' ')
    return `<span class="${lineClasses}"><span class="line-number">${number}</span><span class="line-content">${line}</span></span>`
  }

  return `<span${classes ? ` class="${classes}"` : ''}><span class="line-number">${number}  </span>${line}</span>`
}

const diffLineClass = (line: string) => {
  if (line.startsWith('+++') || line.startsWith('---') || line.startsWith('@@')) {
    return 'diff-coord'
  }
  if (line.startsWith('+')) {
    return 'diff-inserted'
  }
  if (line.startsWith('-')) {
    return 'diff-deleted'
  }
  return ''
}

const trimTrailingEmptyLine = (lines: string[]) =>
  lines.length > 1 && lines[lines.length - 1] === ''
    ? lines.slice(0, -1)
    : lines

const SPAN_CLOSE = '</span>'

// Split highlighted HTML on newlines and close/reopen any spans that
// Prism left open across a line (e.g. Markdown fenced code blocks).
const splitHighlightedHtml = (html: string): string[] => {
  const lines: string[] = []
  const openTags: string[] = []
  let current = ''
  let i = 0

  while (i < html.length) {
    if (html[i] === '\n') {
      lines.push(current + SPAN_CLOSE.repeat(openTags.length))
      current = openTags.join('')
      i += 1
      continue
    }

    if (html.startsWith(SPAN_CLOSE, i)) {
      current += SPAN_CLOSE
      openTags.pop()
      i += SPAN_CLOSE.length
      continue
    }

    if (html.startsWith('<span', i)) {
      const end = html.indexOf('>', i)
      if (end === -1) {
        current += html.slice(i)
        break
      }
      const tag = html.slice(i, end + 1)
      current += tag
      openTags.push(tag)
      i = end + 1
      continue
    }

    current += html[i]
    i += 1
  }

  lines.push(current + SPAN_CLOSE.repeat(openTags.length))
  return lines
}

// Format code with Prism and add line numbers/highlights
const formattedCode = computed(() => {
  // Initialize Prism language extensions
  Prism.languages?.insertBefore('json', 'punctuation', punctuation)
  Prism.languages?.insertBefore('json', 'string', string)
  Prism.languages?.insertBefore('js', 'punctuation', punctuation)
  Prism.languages?.insertBefore('js', 'string', string)
  Prism.languages?.insertBefore('ts', 'punctuation', punctuation)
  Prism.languages?.insertBefore('ts', 'string', string)

  const codeToFormat = activeCode.value
  const grammar = Prism.languages[props.language]

  if (props.language === 'treeview') {
    const formatted = grammar
      ? Prism.highlight(codeToFormat, grammar, props.language)
      : escapeHtml(codeToFormat)

    return formatted.split('<br /></span>')
      .map((line, num) => {
        return `<span ${props.highlights.includes(num + 1) ? 'class="highlight"' : ''}>${line}</span></span>`
      })
      .slice(0, -1)
      .join('\n')
  }

  // Highlight diffs per line so + / - groups don't break line-number wrapping
  if (props.language === 'diff') {
    return sourceLines.value
      .map((line, num) => {
        const highlighted = grammar
          ? Prism.highlight(line, grammar, 'diff')
          : escapeHtml(line)
        return wrapNumberedLine(highlighted, num, diffLineClass(line))
      })
      .join('\n')
  }

  // Wrap languages: highlight the full document so Markdown fences keep
  // cross-line context, then split HTML without breaking open tokens
  if (wrapsText.value) {
    const highlighted = grammar
      ? Prism.highlight(codeToFormat, grammar, props.language)
      : escapeHtml(codeToFormat)

    return trimTrailingEmptyLine(splitHighlightedHtml(highlighted))
      .map((line, num) => wrapNumberedLine(line, num))
      .join('\n')
  }

  const formatted = grammar
    ? Prism.highlight(codeToFormat, grammar, props.language)
    : escapeHtml(codeToFormat)

  return trimTrailingEmptyLine(formatted.split('\n'))
    .map((line, num) => wrapNumberedLine(line, num))
    .join('\n')
})
</script>

<style>
@reference "../../assets/css/main.css";

/* all code block styles */
@import '~/assets/css/prism.css';
@import '~/assets/css/prism-treeview.css';
@import '~/assets/css/icons.css';

.code-block {
  @apply mt-8 mb-10 font-mono font-normal text-sm border-2 border-border;
}
.code-scroll {
  overflow-x: auto;
}

.code-toolbar > .filename {
  padding-inline-start: 1.75em;
}

.code-toolbar > .filename::before {
  height: 1.5em;
}
.code-scroll-wrap {
  overflow-x: hidden;
}
.copyToolbar:hover .copyLabel {
  opacity: 100%
}
</style>
